/**
 * ProjectCreate-new.tsx
 * Redesigned Create Project page — wizard host.
 * Manages the final submission sequence after the wizard collects all data.
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Alert from 'react-bootstrap/Alert';
import { useProjects } from '../contexts/ProjectContext';
import ProjectCreateFormNew, { WizardFinalData } from '../components/projects/ProjectCreateForm-new';
import { getGuidedTourStepIndexById } from '../tour/dashboardGuidedTourConfig';
import { isDashboardGuidedTourInProgress, markGuidedTourProjectCreated } from '../tour/guidedTourStorage';
import { formatApiError } from '../utils/apiError';
import { projectTabRoute } from '../utils/routes';
import './ProjectCreate-new.css';

/* ============================================================
   ProjectCreatePageNew — page-level wrapper
   ============================================================ */

/**
 * ProjectCreatePageNew
 * Hosts the 4-step wizard form.
 * On final submission, creates the project and optional knowledge inputs atomically, then redirects.
 */
function ProjectCreatePageNew(): React.ReactElement
{
    const navigate = useNavigate();
    const { createProjectWithKnowledge } = useProjects();

    const [submitting, setSubmitting] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);

    /**
     * handleWizardComplete
     * Receives all wizard-collected data and runs the full creation sequence.
     */
    async function handleWizardComplete(data: WizardFinalData): Promise<void>
    {
        setSubmitting(true);
        setError(null);

        try
        {
            const project = await createProjectWithKnowledge(data.projectPayload, {
                domainFiles: data.domainFiles,
                enterpriseKnowledge: data.enterpriseKnowledge
                    ? {
                        summary: data.enterpriseKnowledge.summary || null,
                        internal_systems: data.enterpriseKnowledge.internal_systems,
                        acronyms: data.enterpriseKnowledge.acronyms,
                        process_notes: data.enterpriseKnowledge.process_notes || null,
                        testing_notes: data.enterpriseKnowledge.testing_notes || null,
                    }
                    : null,
                enterpriseFiles: data.enterpriseFiles,
            });

            if (isDashboardGuidedTourInProgress())
            {
                markGuidedTourProjectCreated(project.id, getGuidedTourStepIndexById('upload-docs-section'));
                navigate(projectTabRoute(project.id, 'requirements'));
                return;
            }

            navigate(projectTabRoute(project.id, 'requirements'));
        }
        catch (err: any)
        {
            setError(formatApiError(err, 'Failed to create project.'));
        }
        finally
        {
            setSubmitting(false);
        }
    }

    return (
        <div className="project-create-page">

            {/* API-level error shown above the wizard */}
            {error && (
                <Alert variant="danger" className="mb-3" style={{ maxWidth: 760, width: '100%' }}>
                    {error}
                </Alert>
            )}

            {/* 4-step wizard form */}
            <ProjectCreateFormNew
                onWizardComplete={handleWizardComplete}
                submitting={submitting}
            />

        </div>
    );
}

export default ProjectCreatePageNew;
